import React, { useRef, useEffect } from "react";
import { Graphics, Text, Container } from "@pixi/react";
import { ARROW_STATUS_COLORS, ARROW_STATUS_HEX } from "./constants";

export const lerpNumber = (from, to, t) => from + (to - from) * t;

export const toFiniteNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

export const sameOwner = (ownerId, currentUserId) =>
  ownerId != null &&
  currentUserId != null &&
  String(ownerId) === String(currentUserId);

export const cloneAttackArrow = (arrow) => ({
  ...arrow,
  path: Array.isArray(arrow?.path)
    ? arrow.path.map((p) => ({
        x: toFiniteNumber(p?.x, 0),
        y: toFiniteNumber(p?.y, 0),
      }))
    : [],
  opposingForces: Array.isArray(arrow?.opposingForces)
    ? arrow.opposingForces.map((f) => ({ ...f }))
    : [],
});

export const getAttackArrowKey = (arrow, idx) => arrow?.id || `idx-${idx}`;

export const canInterpolateAttackArrows = (fromArrow, toArrow) => {
  if (!fromArrow || !toArrow) return false;
  if (!Array.isArray(fromArrow.path) || !Array.isArray(toArrow.path)) return false;
  if (fromArrow.path.length !== toArrow.path.length) return false;
  for (let i = 0; i < toArrow.path.length; i++) {
    const fromPoint = fromArrow.path[i];
    const toPoint = toArrow.path[i];
    if (!fromPoint || !toPoint) return false;
    if (fromPoint.x !== toPoint.x || fromPoint.y !== toPoint.y) return false;
  }
  return true;
};

export const interpolateAttackArrow = (fromArrow, toArrow, t) => {
  if (!canInterpolateAttackArrows(fromArrow, toArrow)) return toArrow;

  const fallbackX = toFiniteNumber(toArrow.path?.[0]?.x, 0);
  const fallbackY = toFiniteNumber(toArrow.path?.[0]?.y, 0);
  const fromHeadX = toFiniteNumber(fromArrow.headX, fallbackX);
  const fromHeadY = toFiniteNumber(fromArrow.headY, fallbackY);
  const toHeadX = toFiniteNumber(toArrow.headX, fallbackX);
  const toHeadY = toFiniteNumber(toArrow.headY, fallbackY);
  const fromIdx = toFiniteNumber(fromArrow.currentIndex, 1);
  const toIdx = toFiniteNumber(toArrow.currentIndex, 1);
  const fromFrontWidth = toFiniteNumber(fromArrow.frontWidth, 0);
  const toFrontWidth = toFiniteNumber(toArrow.frontWidth, 0);
  const fromDensity = toFiniteNumber(fromArrow.effectiveDensityAtFront, 0);
  const toDensity = toFiniteNumber(toArrow.effectiveDensityAtFront, 0);
  const fromPower = toFiniteNumber(fromArrow.remainingPower, 0);
  const toPower = toFiniteNumber(toArrow.remainingPower, 0);

  return {
    ...toArrow,
    headX: lerpNumber(fromHeadX, toHeadX, t),
    headY: lerpNumber(fromHeadY, toHeadY, t),
    currentIndex: lerpNumber(fromIdx, toIdx, t),
    frontWidth: lerpNumber(fromFrontWidth, toFrontWidth, t),
    effectiveDensityAtFront: lerpNumber(fromDensity, toDensity, t),
    remainingPower: lerpNumber(fromPower, toPower, t),
  };
};

// Simplify arrow path by keeping every Nth point plus start/end
export const simplifyArrowPath = (path, n = 3) => {
  if (!path || path.length < 2) return path;
  const result = [path[0]];
  for (let i = n; i < path.length - 1; i += n) {
    result.push(path[i]);
  }
  if (path.length > 1) {
    result.push(path[path.length - 1]);
  }
  return result;
};

// Helper: interpolate position along a polyline path
export const interpolateAlongPath = (path, t, endIdx, cellSize) => {
  if (!path || path.length < 2) return null;
  const last = Math.min(endIdx ?? path.length - 1, path.length - 1);
  // Compute total length of path segments 0..last
  let totalLen = 0;
  const segLens = [];
  for (let i = 1; i <= last; i++) {
    const dx = (path[i].x - path[i - 1].x) * cellSize;
    const dy = (path[i].y - path[i - 1].y) * cellSize;
    const len = Math.hypot(dx, dy);
    segLens.push(len);
    totalLen += len;
  }
  if (totalLen === 0) return null;
  let target = t * totalLen;
  let acc = 0;
  for (let i = 0; i < segLens.length; i++) {
    if (acc + segLens[i] >= target) {
      const frac = (target - acc) / segLens[i];
      return {
        x: (path[i].x + (path[i + 1].x - path[i].x) * frac) * cellSize + cellSize / 2,
        y: (path[i].y + (path[i + 1].y - path[i].y) * frac) * cellSize + cellSize / 2,
      };
    }
    acc += segLens[i];
  }
  return {
    x: path[last].x * cellSize + cellSize / 2,
    y: path[last].y * cellSize + cellSize / 2,
  };
};

// Simple thin-line arrow for drawing-in-progress and defend arrows
export const renderArrowPath = (
  path,
  cellSize,
  type,
  isActive,
  key,
  troopCount = null,
  scale = 1,
  colorOverride = null
) => {
  if (!path || path.length < 2) return null;

  const color = colorOverride ?? (type === "attack" ? 0xff4444 : 0x4444ff);
  const alpha = isActive ? 0.8 : 0.6;

  const midIndex = Math.floor(path.length / 2);
  const midPoint = path[midIndex];
  const midX = midPoint.x * cellSize + cellSize / 2;
  const midY = midPoint.y * cellSize + cellSize / 2;
  const invScale = 1 / Math.max(0.35, scale || 1);

  return (
    <React.Fragment key={key}>
      <Graphics
        zIndex={200}
        draw={(g) => {
          g.clear();
          g.lineStyle(4, color, alpha);
          const startX = path[0].x * cellSize + cellSize / 2;
          const startY = path[0].y * cellSize + cellSize / 2;
          g.moveTo(startX, startY);
          for (let i = 1; i < path.length; i++) {
            g.lineTo(path[i].x * cellSize + cellSize / 2, path[i].y * cellSize + cellSize / 2);
          }
          if (path.length >= 2) {
            const last = path[path.length - 1];
            const prev = path[path.length - 2];
            const endX = last.x * cellSize + cellSize / 2;
            const endY = last.y * cellSize + cellSize / 2;
            const angle = Math.atan2(endY - (prev.y * cellSize + cellSize / 2), endX - (prev.x * cellSize + cellSize / 2));
            const arrowSize = cellSize * 0.8;
            g.beginFill(color, alpha);
            g.moveTo(endX, endY);
            g.lineTo(endX - arrowSize * Math.cos(angle - Math.PI / 6), endY - arrowSize * Math.sin(angle - Math.PI / 6));
            g.lineTo(endX - arrowSize * 0.5 * Math.cos(angle), endY - arrowSize * 0.5 * Math.sin(angle));
            g.lineTo(endX - arrowSize * Math.cos(angle + Math.PI / 6), endY - arrowSize * Math.sin(angle + Math.PI / 6));
            g.lineTo(endX, endY);
            g.endFill();
          }
        }}
      />
      {troopCount !== null && (
        <Container
          x={midX}
          y={midY - cellSize * 0.8}
          zIndex={202}
          scale={{ x: invScale, y: invScale }}
        >
          <Graphics
            zIndex={201}
            draw={(g) => {
              g.clear();
              const radius = Math.max(10, Math.min(18, cellSize * 0.55));
              g.beginFill(0x000000, 0.75);
              g.drawCircle(0, 0, radius);
              g.endFill();
              g.lineStyle(2, color, 1);
              g.drawCircle(0, 0, radius);
            }}
          />
          <Text
            text={Math.round(troopCount).toString()}
            x={0}
            y={0}
            anchor={0.5}
            zIndex={202}
            style={{
              fontFamily: "Barlow Semi Condensed, system-ui",
              fill: "#ffffff",
              fontSize: Math.max(14, cellSize * 0.6),
              fontWeight: "700",
            }}
          />
        </Container>
      )}
    </React.Fragment>
  );
};

// Broad wedge arrow for active attack arrows (documentary war style)
export const renderArrowV2 = (arrow, cellSize, key, scale) => {
  if (!arrow?.path || arrow.path.length < 2) return null;

  const path = arrow.path;
  const status = arrow.status || "advancing";
  const color = ARROW_STATUS_COLORS[status] || 0x44cc44;
  const hexColor = ARROW_STATUS_HEX[status] || "#44cc44";
  const frontWidth = Math.max(2, arrow.frontWidth || 3);
  const halfW = (frontWidth / 2) * cellSize;
  const isDensityArrow = arrow.troopCommitment != null && arrow.troopCommitment > 0;
  const troopCount = isDensityArrow
    ? (arrow.effectiveDensityAtFront || 0)
    : (arrow.remainingPower || 0);
  const headX = (arrow.headX ?? path[path.length - 1].x) * cellSize + cellSize / 2;
  const headY = (arrow.headY ?? path[path.length - 1].y) * cellSize + cellSize / 2;
  const opposingForces = arrow.opposingForces || [];

  return (
    <React.Fragment key={key}>
      <Graphics
        zIndex={200}
        draw={(g) => {
          g.clear();

          const startX = path[0].x * cellSize + cellSize / 2;
          const startY = path[0].y * cellSize + cellSize / 2;

          // Build left and right outlines by offsetting path perpendicular
          const leftPts = [];
          const rightPts = [];
          let lastDx = 0, lastDy = 0;
          for (let i = 0; i < path.length; i++) {
            const px = path[i].x * cellSize + cellSize / 2;
            const py = path[i].y * cellSize + cellSize / 2;
            const t = path.length > 1 ? i / (path.length - 1) : 0;
            // Spearhead: wide at origin, narrowing to a point at the front
            const w = halfW * Math.max(0.08, 1.0 - 0.88 * t);

            let dx, dy;
            if (i < path.length - 1) {
              dx = path[i + 1].x - path[i].x;
              dy = path[i + 1].y - path[i].y;
            } else {
              dx = path[i].x - path[i - 1].x;
              dy = path[i].y - path[i - 1].y;
            }
            lastDx = dx; lastDy = dy;
            const len = Math.hypot(dx, dy) || 1;
            const perpX = -dy / len;
            const perpY = dx / len;

            leftPts.push({ x: px + perpX * w, y: py + perpY * w });
            rightPts.push({ x: px - perpX * w, y: py - perpY * w });
          }

          // Draw filled wedge polygon
          g.beginFill(color, 0.25);
          g.lineStyle(2, color, 0.6);
          g.moveTo(leftPts[0].x, leftPts[0].y);
          for (let i = 1; i < leftPts.length; i++) g.lineTo(leftPts[i].x, leftPts[i].y);
          for (let i = rightPts.length - 1; i >= 0; i--) g.lineTo(rightPts[i].x, rightPts[i].y);
          g.lineTo(leftPts[0].x, leftPts[0].y);
          g.endFill();

          // Arrowhead at the tip — wings as wide as the arrow base
          const tipX = path[path.length - 1].x * cellSize + cellSize / 2;
          const tipY = path[path.length - 1].y * cellSize + cellSize / 2;
          const tipLen = Math.hypot(lastDx, lastDy) || 1;
          const fwdX = lastDx / tipLen;
          const fwdY = lastDy / tipLen;
          const perpTipX = -fwdY;
          const perpTipY = fwdX;
          const arrowLen = Math.max(cellSize * 1.5, halfW * 0.6);
          const arrowWing = halfW; // match the full base width of the wedge
          g.beginFill(color, 0.7);
          g.lineStyle(0);
          g.moveTo(tipX + fwdX * arrowLen, tipY + fwdY * arrowLen);
          g.lineTo(tipX + perpTipX * arrowWing, tipY + perpTipY * arrowWing);
          g.lineTo(tipX - perpTipX * arrowWing, tipY - perpTipY * arrowWing);
          g.lineTo(tipX + fwdX * arrowLen, tipY + fwdY * arrowLen);
          g.endFill();

          // Center line
          g.lineStyle(2, color, 0.5);
          g.moveTo(startX, startY);
          for (let i = 1; i < path.length; i++) {
            g.lineTo(path[i].x * cellSize + cellSize / 2, path[i].y * cellSize + cellSize / 2);
          }

          // Phase markers
          for (let i = 1; i < path.length; i++) {
            const wx = path[i].x * cellSize + cellSize / 2;
            const wy = path[i].y * cellSize + cellSize / 2;
            const reached = i < (arrow.currentIndex || 1);
            g.lineStyle(1, reached ? 0xffffff : 0x888888, 0.6);
            g.beginFill(reached ? 0xffffff : 0x444444, 0.4);
            g.drawCircle(wx, wy, cellSize * 0.2);
            g.endFill();
          }
        }}
      />
      {/* Arrow status badge at head — fixed screen size via inverse scale */}
      {(() => {
        const invScale = 1 / Math.max(0.35, scale || 1);
        const labelAlpha = scale > 2 ? Math.max(0.3, 1.0 - (scale - 2) * 0.15) : 0.85;
        const badgeW = 120;
        const badgeH = opposingForces.length > 0 ? 52 : 38;
        const commitment = arrow.troopCommitment || 0;
        return (
          <Container
            x={headX}
            y={headY - cellSize * 1.2}
            zIndex={211}
            alpha={labelAlpha}
            scale={{ x: invScale, y: invScale }}
          >
            {/* Dark background pill */}
            <Graphics
              draw={(g) => {
                g.clear();
                g.beginFill(0x000000, 0.7);
                g.drawRoundedRect(-badgeW / 2, -badgeH / 2, badgeW, badgeH, 6);
                g.endFill();
                // Status color stripe at top
                g.beginFill(color, 0.9);
                g.drawRoundedRect(-badgeW / 2, -badgeH / 2, badgeW, 3, 1);
                g.endFill();
                // Commitment bar background
                const barY = opposingForces.length > 0 ? 8 : 6;
                g.beginFill(0x333333, 0.8);
                g.drawRoundedRect(-badgeW / 2 + 8, barY, badgeW - 16, 4, 2);
                g.endFill();
                // Commitment bar fill
                const fillW = (badgeW - 16) * Math.min(1, commitment);
                if (fillW > 0) {
                  g.beginFill(color, 0.9);
                  g.drawRoundedRect(-badgeW / 2 + 8, barY, fillW, 4, 2);
                  g.endFill();
                }
                // Status dot
                g.beginFill(color, 1);
                g.drawCircle(-badgeW / 2 + 14, -4, 4);
                g.endFill();
              }}
            />
            {/* Troop count text */}
            <Text
              text={`${Math.round(troopCount).toLocaleString()}  ${Math.round(commitment * 100)}%`}
              x={-badgeW / 2 + 24}
              y={-10}
              anchor={{ x: 0, y: 0.5 }}
              style={{
                fontFamily: "system-ui, -apple-system, sans-serif",
                fill: "#ffffff",
                fontSize: 13,
                fontWeight: "600",
              }}
            />
            {/* Opposition text */}
            {opposingForces.length > 0 && (
              <Text
                text={opposingForces.map((o) => `vs ${o.nationName}`).join(" ")}
                x={0}
                y={badgeH / 2 - 8}
                anchor={{ x: 0.5, y: 0.5 }}
                style={{
                  fontFamily: "system-ui, -apple-system, sans-serif",
                  fill: "#ff6666",
                  fontSize: 11,
                  fontWeight: "400",
                }}
              />
            )}
          </Container>
        );
      })()}
    </React.Fragment>
  );
};

// Animated marching dots along arrow paths
export const ArrowTroopFlow = React.memo(({ arrow, cellSize, scale, visibleBounds }) => {
  const graphicsRef = useRef(null);
  const phaseRef = useRef(0);
  const rafRef = useRef(null);

  const path = arrow?.path;
  const status = arrow?.status || "advancing";
  const color = ARROW_STATUS_COLORS[status] || 0x44cc44;
  const density = arrow?.effectiveDensityAtFront || 0;
  const commitment = arrow?.troopCommitment || 0;
  const endIdx = arrow?.currentIndex || (path ? path.length - 1 : 0);

  useEffect(() => {
    if (!path || path.length < 2) return;
    let lastTime = performance.now();

    const animate = (now) => {
      const dt = (now - lastTime) / 1000;
      lastTime = now;
      // Speed: faster with higher commitment
      const speed = 0.15 + commitment * 0.35;
      phaseRef.current = (phaseRef.current + dt * speed) % 1;

      const g = graphicsRef.current;
      if (!g) {
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      g.clear();

      // Dot parameters
      const dotCount = Math.max(8, Math.min(50, Math.round(10 + density * 0.15)));
      const spacing = 1 / dotCount;
      const dotRadius = cellSize * 0.12;
      const baseAlpha = 0.5 + Math.min(0.4, density / 500);

      for (let i = 0; i < dotCount; i++) {
        const t = (i * spacing + phaseRef.current * spacing) % 1;
        const pos = interpolateAlongPath(path, t, endIdx, cellSize);
        if (!pos) continue;

        // Visibility culling
        if (visibleBounds) {
          const cx = pos.x / cellSize;
          const cy = pos.y / cellSize;
          if (cx < visibleBounds.minX - 1 || cx > visibleBounds.maxX + 1 ||
              cy < visibleBounds.minY - 1 || cy > visibleBounds.maxY + 1) continue;
        }

        g.beginFill(color, baseAlpha);
        g.drawCircle(pos.x, pos.y, dotRadius);
        g.endFill();
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [path, cellSize, endIdx, commitment, density, color, visibleBounds]);

  if (!path || path.length < 2) return null;

  return (
    <Graphics
      ref={graphicsRef}
      zIndex={195}
    />
  );
});
